import { TrendingUp, TrendingDown, Minus, Loader2, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { useSubmarketRentGrowth } from '../../hooks/useRentGrowth';
import { SectionTitle } from '../Luxury';

const signalConfig: Record<string, { bg: string; text: string; border: string; label: string; ring: string }> = {
  BUY: { bg: 'bg-emerald/15', text: 'text-emerald', border: 'border-emerald/40', label: 'BUY', ring: 'stroke-emerald' },
  HOLD: { bg: 'bg-gold/15', text: 'text-gold', border: 'border-gold/40', label: 'HOLD', ring: 'stroke-gold' },
  WAIT: { bg: 'bg-crimson/15', text: 'text-crimson', border: 'border-crimson/40', label: 'WAIT', ring: 'stroke-crimson' },
};

function ScoreRing({ score, signal }: { score: number; signal: string }) {
  const radius = 28;
  const circumference = 2 * Math.PI * radius;
  const pct = Math.min(score, 100) / 100;
  const offset = circumference * (1 - pct);
  const colors = signalConfig[signal] || signalConfig.HOLD;

  return (
    <div className="relative w-20 h-20 flex-shrink-0">
      <svg className="w-full h-full -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={radius} fill="none" strokeWidth="4" className="stroke-graphite" />
        <circle
          cx="32" cy="32" r={radius} fill="none" strokeWidth="4"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className={colors.ring}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-lg font-bold font-mono tabular-nums ${colors.text}`}>{score.toFixed(0)}</span>
      </div>
    </div>
  );
}

function AccelerationBadge({ tier }: { tier: string | null }) {
  if (!tier) return null;

  const config: Record<string, { icon: React.ReactNode; color: string; bg: string }> = {
    Accelerating: {
      icon: <ArrowUpRight className="w-3 h-3" />,
      color: 'text-emerald',
      bg: 'bg-emerald/10',
    },
    Stable: {
      icon: <Minus className="w-3 h-3" />,
      color: 'text-stone-700',
      bg: 'bg-stone-500/10',
    },
    Decelerating: {
      icon: <ArrowDownRight className="w-3 h-3" />,
      color: 'text-crimson',
      bg: 'bg-crimson/10',
    },
  };

  const c = config[tier] || config.Stable;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm font-semibold uppercase tracking-wider ${c.color} ${c.bg}`}>
      {c.icon}
      {tier}
    </span>
  );
}

function TrendArrow({ accelTier }: { accelTier: string | null }) {
  if (accelTier === 'Accelerating') return <TrendingUp className="w-4 h-4 text-emerald" />;
  if (accelTier === 'Decelerating') return <TrendingDown className="w-4 h-4 text-crimson" />;
  return <Minus className="w-4 h-4 text-stone-700" />;
}

interface RentGrowthCardProps {
  submarketName: string;
  marketId?: string;
}

/**
 * Enhanced card for property detail view showing submarket rent growth intelligence:
 * signal badge, composite ring, CAGR with trend, top drivers with bars, and narrative.
 */
export function RentGrowthCard({ submarketName, marketId }: RentGrowthCardProps) {
  const { data, isLoading, error } = useSubmarketRentGrowth(submarketName);

  if (isLoading) {
    return (
      <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
        <div className="flex items-center justify-center py-4 text-stone-700">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading rent growth intelligence...
        </div>
      </div>
    );
  }

  // Graceful empty state for non-Philadelphia markets
  if (error || !data) {
    if (marketId && marketId !== 'philadelphia') {
      return (
        <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
          <SectionTitle icon={<TrendingUp className="w-4 h-4" />}>
            Rent Growth Intelligence
          </SectionTitle>
          <p className="text-sm text-stone-700 text-center py-4">
            Rent growth intelligence is not yet available for this market. This data is currently Philadelphia-only.
          </p>
        </div>
      );
    }
    return null;
  }

  const signal = signalConfig[data.signal] || signalConfig.HOLD;
  const topDrivers = data.drivers.slice(0, 3);

  // Build narrative
  const growthStr = data.cagr_5y !== null ? `${data.cagr_5y.toFixed(1)}%` : 'N/A';
  const topFactors = topDrivers
    .filter((d) => d.contribution_pct && d.contribution_pct > 0)
    .map((d) => d.name.toLowerCase())
    .join(', ');

  const accelPhrase =
    data.accel_tier === 'Accelerating' ? ' Growth is accelerating.' :
    data.accel_tier === 'Decelerating' ? ' Growth is decelerating.' : '';

  const narrative = `This submarket grew ${growthStr} annually (5Y CAGR)${topFactors ? `, driven by ${topFactors}` : ''}.${accelPhrase}`;

  return (
    <div className="bg-charcoal p-6 rounded-lg border border-slate-dark">
      {/* Header with signal badge */}
      <div className="flex items-center justify-between mb-4">
        <SectionTitle icon={<TrendingUp className="w-4 h-4" />}>
          Rent Growth Intelligence
        </SectionTitle>
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold uppercase tracking-wider border ${signal.bg} ${signal.text} ${signal.border}`}
        >
          {signal.label}
        </span>
      </div>

      {/* Composite ring + CAGR + Vacancy */}
      <div className="flex items-center gap-5 mb-5">
        <ScoreRing score={data.composite_score} signal={data.signal} />

        <div className="flex-1 grid grid-cols-2 gap-4">
          {/* 5Y CAGR */}
          <div className="bg-graphite/50 rounded-lg p-3">
            <p className="text-sm uppercase tracking-wider text-stone-700 mb-1">5Y CAGR</p>
            <div className="flex items-center gap-2">
              <TrendArrow accelTier={data.accel_tier} />
              <span className="text-2xl font-bold font-mono tabular-nums text-ivory">
                {data.cagr_5y !== null ? `${data.cagr_5y.toFixed(1)}%` : '--'}
              </span>
            </div>
            <div className="mt-1">
              <AccelerationBadge tier={data.accel_tier} />
            </div>
          </div>

          {/* Vacancy */}
          <div className="bg-graphite/50 rounded-lg p-3">
            <p className="text-sm uppercase tracking-wider text-stone-700 mb-1">Vacancy</p>
            <p className="text-2xl font-bold font-mono tabular-nums text-ivory">
              {data.vacancy_rate !== null ? `${data.vacancy_rate.toFixed(1)}%` : '--'}
            </p>
            {data.median_rent !== null && (
              <p className="text-sm text-stone-700 mt-1">
                Med. Rent: ${data.median_rent.toLocaleString()}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Top 3 drivers with bar visualization */}
      {topDrivers.length > 0 && (
        <div className="space-y-2 mb-4">
          <p className="text-sm uppercase tracking-wider text-stone-700 font-semibold">
            Top Rent Growth Drivers
          </p>
          {topDrivers.map((driver) => {
            const pct = driver.contribution_pct ?? 0;
            const barColor = pct >= 30 ? 'bg-emerald/70' : pct >= 15 ? 'bg-gold/70' : 'bg-stone-500/70';
            return (
              <div key={driver.name} className="flex items-center gap-3">
                <span className="text-sm text-ivory w-28 truncate" title={driver.name}>{driver.name}</span>
                <div className="flex-1 h-2 bg-graphite rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${barColor}`}
                    style={{ width: `${Math.min(driver.normalized_score || 0, 100)}%` }}
                  />
                </div>
                <span className="text-sm font-mono tabular-nums text-stone-700 w-12 text-right">
                  {pct.toFixed(0)}%
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Narrative */}
      <p className="text-sm text-stone-700 leading-relaxed">{narrative}</p>
    </div>
  );
}
